import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Copy, ExternalLink } from 'lucide-react';

/**
 * Les événements que ce logiciel écoute réellement.
 *
 * Cette liste n'est pas décorative : un événement oublié ne provoque aucune
 * erreur, aucune alerte, aucun message. Le paiement part, Stripe l'encaisse,
 * et l'association ne le voit jamais arriver dans son espace — elle relance
 * une famille qui a payé. C'est le genre de panne qu'on met des semaines à
 * comprendre, parce que rien ne casse.
 *
 * Elle doit rester alignée sur StripeController : tout événement traité
 * là-bas se coche ici, et réciproquement.
 */
const EVENEMENTS: { code: string; role: string }[] = [
  {
    code: 'checkout.session.completed',
    role: 'Un règlement par carte est arrivé : cotisation, billet, don, commande.',
  },
  {
    code: 'payment_intent.succeeded',
    role: 'Un prélèvement SEPA a été honoré par la banque — c’est lui qui solde l’échéance.',
  },
  {
    code: 'payment_intent.payment_failed',
    role: 'Un prélèvement a été rejeté : l’échéance repart, la famille reste relancée.',
  },
];

/**
 * La grille de Stripe, relevée sur stripe.com/fr/pricing le 17 août 2026.
 *
 * Datée, parce qu'elle ne nous appartient pas : Stripe la change quand il
 * veut, et un tarif qu'on affiche sans dire d'où il vient finit par engager
 * l'éditeur sur un chiffre périmé.
 */
const TARIFS: { quoi: string; combien: string }[] = [
  { quoi: 'Carte standard, Espace économique européen', combien: '1,5 % + 0,25 €' },
  { quoi: 'Carte premium ou professionnelle, EEE', combien: '2,8 % + 0,25 €' },
  { quoi: 'Carte britannique', combien: '2,5 % + 0,25 € + 2 %' },
  { quoi: 'Carte hors EEE', combien: '3,15 % + 0,25 € + 2 %' },
  { quoi: 'Prélèvement SEPA', combien: '0,35 €' },
  { quoi: 'Contestation de paiement', combien: '20 €' },
];

/**
 * La description d'activité que Stripe réclame, prête à adapter.
 *
 * L'écran arrive avec un exemple tout fait — des applications mobiles de
 * productivité et de remise en forme — que beaucoup laissent en place faute
 * de savoir quoi écrire. Une description qui ne correspond pas à l'activité
 * réelle finit par bloquer le compte au premier contrôle.
 */
const DESCRIPTION_TYPE =
  "Association loi 1901 déclarée en préfecture. Nous encaissons en ligne les " +
  "cotisations annuelles de nos adhérents, la billetterie de nos manifestations " +
  "(repas, concerts, tournois), les dons de nos soutiens et les participations de " +
  "nos partenaires. Les prestations sont fournies à des dates connues à l'avance ; " +
  "les cotisations couvrent une saison entière.";

/** Une valeur qu'on recopie sans se tromper : un bouton plutôt qu'une sélection à la souris. */
function ACopier({ valeur, multiligne = false }: { valeur: string; multiligne?: boolean }) {
  const [copie, setCopie] = useState(false);

  return (
    <div className={`flex gap-2 ${multiligne ? 'items-start' : 'items-stretch'}`}>
      <span
        className={`flex-1 min-w-0 rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-2 text-xs text-gray-800 dark:text-gray-200 ${
          multiligne ? 'leading-relaxed' : 'font-mono overflow-x-auto whitespace-nowrap'
        }`}
      >
        {valeur}
      </span>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(valeur);
          setCopie(true);
          setTimeout(() => setCopie(false), 2000);
        }}
        className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
          multiligne ? 'py-2 self-start' : ''
        }`}
      >
        {copie ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        {copie ? 'Copié' : 'Copier'}
      </button>
    </div>
  );
}

function Etape({
  numero,
  titre,
  children,
}: {
  numero: number;
  titre: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-bold flex items-center justify-center">
        {numero}
      </span>
      <div className="flex-1 min-w-0 space-y-2 pb-4">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{titre}</p>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">{children}</div>
      </div>
    </li>
  );
}

/**
 * Comment l'association ouvre et règle son propre compte Stripe.
 *
 * Le compte est le sien, pas le nôtre, et ce n'est pas un détail
 * administratif : l'argent des adhérents arrive dessus. Nous n'y avons aucun
 * accès, nous ne pouvons pas le créer à sa place, et le jour où elle nous
 * quitte elle part avec.
 *
 * D'où cette notice à l'endroit exact où l'on demande les clés. Une
 * documentation rangée ailleurs se lit après coup, quand la configuration est
 * déjà à moitié faite et à moitié fausse.
 *
 * L'adresse du webhook est celle de cette instance, calculée et non décrite :
 * « remplacez votre-association par votre adresse » est la première phrase
 * qu'on recopie de travers.
 */
export default function NoticeStripe() {
  const [ouverte, setOuverte] = useState(false);

  const adresseWebhook = `${window.location.origin}/api/webhooks/stripe`;

  return (
    <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setOuverte((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Ouvrir et régler votre compte Stripe — la marche à suivre
        </span>
        {ouverte ? (
          <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
        )}
      </button>

      {ouverte && (
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Le compte Stripe est celui de votre association, à son nom et sur son IBAN. Nous n'y
            avons aucun accès : nous ne voyons ni vos paiements ni vos soldes, et le jour où vous
            partez, vous partez avec. Comptez une vingtaine de minutes, et le RIB de l'association
            sous la main.
          </p>

          <PourquoiStripe />

          <ol className="space-y-1">
            <Etape numero={1} titre="Créer le compte">
              <p>
                Sur{' '}
                <a
                  href="https://dashboard.stripe.com/register"
                  target="_blank"
                  rel="noopener"
                  className="text-primary-700 dark:text-primary-400 underline font-medium inline-flex items-center gap-1"
                >
                  dashboard.stripe.com/register
                  <ExternalLink className="w-3 h-3" />
                </a>
                , avec l'adresse de fonction de l'association — jamais l'adresse personnelle d'un
                membre du bureau. Un compte ouvert au nom d'un trésorier devient inaccessible le
                jour où il passe la main.
              </p>
            </Etape>

            <Etape numero={2} titre="Renseigner l'association">
              <p>
                Stripe demande le numéro RNA ou SIRET, l'adresse du siège et l'identité d'un
                dirigeant — c'est la loi anti-blanchiment, pas une curiosité de leur part. L'IBAN
                saisi ici est celui qui recevra les virements.
              </p>
              <p>
                <strong>Catégorie d'activité :</strong> « Organisation à but non lucratif », ou
                « Associations et clubs » selon la formulation proposée.
              </p>
              <p>
                <strong>Description de l'activité :</strong> l'écran arrive avec un exemple tout
                fait — des applications mobiles de productivité et de remise en forme. Il faut le
                remplacer. Voici de quoi partir :
              </p>
              <ACopier valeur={DESCRIPTION_TYPE} multiligne />
              <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-200">
                <strong>Adaptez-la à ce que vous faites réellement.</strong> Si vous ne vendez pas
                de billets, retirez la billetterie ; si vous ne collectez pas de dons, retirez les
                dons. Une description qui ne correspond pas à l'activité se retourne contre vous au
                premier contrôle, et le compte se retrouve suspendu au pire moment — celui où
                l'argent est déjà encaissé.
              </p>
              <p>
                Si vous ouvrez les dons, précisez-le : Stripe s'y intéresse particulièrement, et il
                vaut mieux dire d'emblée si votre association est d'intérêt général et si elle
                délivre des reçus fiscaux.
              </p>
            </Etape>

            <Etape numero={3} titre="Copier les deux clés d'API">
              <p>
                Dans{' '}
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener"
                  className="text-primary-700 dark:text-primary-400 underline font-medium inline-flex items-center gap-1"
                >
                  Développeurs → Clés d'API
                  <ExternalLink className="w-3 h-3" />
                </a>
                . La clé publique (<code className="text-xs">pk_</code>) et la clé secrète (
                <code className="text-xs">sk_</code>) se collent dans les champs ci-dessus.
              </p>
              <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-200">
                La clé secrète ne s'affiche qu'une fois. Si vous la perdez, on n'en récupère pas une
                copie : on en régénère une nouvelle, et l'ancienne cesse de fonctionner.
              </p>
            </Etape>

            <Etape numero={4} titre="Déclarer le webhook">
              <p>
                C'est l'étape qu'on oublie, et la seule dont l'oubli ne se voit pas. Sans elle,
                Stripe encaisse mais ne vous prévient de rien : les règlements n'apparaissent jamais
                dans votre espace, et vous relancez des familles qui ont payé.
              </p>
              <p>
                Dans{' '}
                <a
                  href="https://dashboard.stripe.com/webhooks"
                  target="_blank"
                  rel="noopener"
                  className="text-primary-700 dark:text-primary-400 underline font-medium inline-flex items-center gap-1"
                >
                  Développeurs → Webhooks
                  <ExternalLink className="w-3 h-3" />
                </a>
                , « Ajouter un point de terminaison », et collez cette adresse — c'est celle de votre
                espace :
              </p>
              <ACopier valeur={adresseWebhook} />
              <p className="pt-1">Puis cochez exactement ces événements :</p>
              <ul className="space-y-1.5">
                {EVENEMENTS.map((e) => (
                  <li key={e.code} className="flex gap-2">
                    <Check className="w-3.5 h-3.5 mt-1 shrink-0 text-green-600" />
                    <span>
                      <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                        {e.code}
                      </code>
                      <span className="block text-xs mt-0.5">{e.role}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Etape>

            <Etape numero={5} titre="Copier le secret de signature">
              <p>
                Une fois le point de terminaison créé, Stripe affiche un « secret de signature » qui
                commence par <code className="text-xs">whsec_</code>. Collez-le dans le troisième
                champ ci-dessus. Il sert à vérifier que les messages viennent bien de Stripe et non
                de quelqu'un qui aurait deviné votre adresse.
              </p>
            </Etape>

            <Etape numero={6} titre="Essayer avant d'ouvrir aux adhérents">
              <p>
                Stripe donne deux jeux de clés : test et réel. Commencez par les clés de test
                (préfixe <code className="text-xs">pk_test_</code> /{' '}
                <code className="text-xs">sk_test_</code>) et réglez une cotisation avec la carte de
                démonstration <code className="text-xs">4242 4242 4242 4242</code>, date future et
                cryptogramme au hasard. Rien n'est débité, et vous voyez le règlement se poser.
              </p>
              <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-200">
                Le passage aux clés réelles demande de <strong>refaire l'étape 4</strong> : les
                webhooks du mode test et ceux du mode réel sont deux listes séparées chez Stripe. Une
                configuration de test qui marche ne dit rien de la production.
              </p>
            </Etape>
          </ol>

          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 space-y-2">
            <p className="font-semibold text-gray-800 dark:text-gray-200">Ce que Stripe prélève</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <tbody className="[&_td]:py-1 [&_td:last-child]:text-right [&_td:last-child]:whitespace-nowrap [&_td:last-child]:tabular-nums [&_td:last-child]:font-medium">
                  {TARIFS.map((t) => (
                    <tr key={t.quoi} className="border-b border-gray-200/70 dark:border-gray-700/70 last:border-0">
                      <td>{t.quoi}</td>
                      <td className="text-gray-800 dark:text-gray-200">{t.combien}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              L'Espace économique européen n'est pas la zone euro : une carte suédoise ou danoise
              relève bien du tarif standard.
            </p>
            <p>
              Le prélèvement SEPA est à commission fixe : sur une cotisation de 130 €, il coûte
              0,35 € là où la carte en coûte 2,20 €. Même étalé en trois fois, il reste le moins
              cher — de quoi peser dans le choix que vous laissez aux familles.
            </p>
            <p>
              Ces tarifs sont ceux de Stripe et peuvent changer ; la grille à jour est sur{' '}
              <a
                href="https://stripe.com/fr/pricing"
                target="_blank"
                rel="noopener"
                className="text-primary-700 dark:text-primary-400 underline"
              >
                stripe.com/fr/pricing
              </a>
              . Nous ne prenons aucune commission et ne percevons rien sur ce compte.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Pourquoi Stripe, et pas la solution d'encaissement de votre banque.
 *
 * La question vient de tous les bureaux, et elle est légitime : l'association
 * a déjà une banque, et cette banque vend un service de paiement en ligne.
 * La réponse honnête tient en trois raisons — dont une qui nous concerne, et
 * qu'il vaut mieux dire que taire.
 */
function PourquoiStripe() {
  const [ouverte, setOuverte] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setOuverte((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60"
      >
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          Pourquoi Stripe, et pas l'encaissement en ligne de notre banque ?
        </span>
        {ouverte ? (
          <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
        )}
      </button>

      {ouverte && (
        <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 space-y-3 text-sm text-gray-600 dark:text-gray-400 [&_strong]:text-gray-900 dark:[&_strong]:text-gray-100">
          <p>
            La question est légitime : votre banque tient déjà vos comptes, et elle vend un service
            de paiement en ligne. Voici les trois raisons, dont une qui nous concerne — autant la
            dire.
          </p>

          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">
              1. Un abonnement mensuel, là où Stripe ne facture que ce qui passe
            </p>
            <p>
              L'encaissement en ligne d'une banque passe par un <strong>contrat de vente à
              distance</strong>, distinct du terminal de carte que vous avez peut-être déjà. Il se
              paie en général en deux morceaux : un abonnement mensuel pour la page de paiement, et
              une commission par transaction.
            </p>
            <p className="mt-1.5">
              La commission d'une banque est souvent plus basse que les 1,5 % de Stripe.{' '}
              <strong>C'est l'abonnement qui décide.</strong> Une association qui encaisse 5 000 €
              par an paiera cet abonnement douze fois, qu'elle vende ou non — et en février, une
              association ne vend rien. Chez Stripe, un mois sans encaissement coûte zéro.
            </p>
            <p className="mt-1.5">
              Le calcul s'inverse au-delà d'un certain volume. Si vous encaissez plusieurs dizaines
              de milliers d'euros par an,{' '}
              <strong>demandez un devis à votre banque et comparez</strong> : nous n'avons aucun
              intérêt à ce que vous restiez chez Stripe, nous ne touchons rien dessus.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">
              2. La sécurité des cartes ne repose ni sur vous ni sur nous
            </p>
            <p>
              Un numéro de carte n'entre jamais dans ce logiciel : la page de paiement est celle de
              Stripe, et vos adhérents la quittent une fois payés. Ce détail change tout —{' '}
              <strong>la norme bancaire PCI-DSS ne s'applique alors qu'à sa forme la plus légère</strong>.
            </p>
            <p className="mt-1.5">
              Héberger nous-mêmes le formulaire ferait basculer votre association et nous dans un
              autre régime : questionnaire annuel, analyses de vulnérabilité, et la responsabilité
              d'une fuite de numéros de carte. Aucun bénévole ne devrait avoir à porter cela.
            </p>
            <p className="mt-1.5">
              L'authentification forte du payeur — le code de votre banque à la validation — suit
              une réglementation européenne qui bouge régulièrement. Stripe la met à jour ; sur une
              passerelle bancaire, c'est au commerçant de suivre.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">
              3. Il y a des dizaines de banques, et un seul logiciel
            </p>
            <p>
              Cette raison est la nôtre, et c'est la plus déterminante. Chaque banque française a sa
              propre passerelle, ses propres codes, sa propre documentation.{' '}
              <strong>Nous savons en brancher une, pas quarante.</strong> Le jour où nous
              soutiendrions la passerelle de votre banque, il faudrait la maintenir seule, pour vous
              seuls — et l'association suivante arriverait avec une autre banque.
            </p>
            <p className="mt-1.5">
              Le raccourci consistant à tout encaisser sur un compte à nous pour vous reverser
              ensuite n'existe pas : cela ferait de nous un établissement de paiement, agréé et
              contrôlé. <strong>Votre argent va directement de votre payeur à votre IBAN</strong>,
              sans jamais passer par nos mains. C'est la seule position tenable — et c'est aussi la
              seule qui vous garantisse de partir avec vos encaissements le jour où vous nous
              quittez.
            </p>
          </div>

          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs">
            <p>
              <strong className="text-gray-800 dark:text-gray-200">Et le délai.</strong> Un contrat
              de vente à distance se négocie avec un conseiller, se signe, puis s'installe : comptez
              plusieurs semaines. Le compte Stripe s'ouvre dans l'après-midi. Pour une association
              qui veut ouvrir ses inscriptions avant la rentrée, ce n'est pas un détail.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
